import React from 'react'
import { Accordion } from 'radix-ui'
import { AccordionItem, ToolkitPageLayout } from '../../components'
import {
  AmbulatoryClinic,
  Clinic,
  CommunityHealthcareWorker,
  Stethoscope
} from 'shared/HealthIcons'
import { Link } from 'react-router-dom'

// Image Imports from src/assets/index.ts
import {
  SickleCellBlood,
  BloodCloseUp,
  PatientAndPractionerTalking,
  SmilingDoctorCloseUp,
  SmilingSittingDoctor,
  SmilingPatientOnBed
} from 'assets' // Assuming 'assets' is aliased or directly accessible
import { useState, useEffect } from 'react'

const LOCAL_STORAGE_KEY = 'toolkitAccordionOpenSection'

export const ToolkitHome: React.FC = () => {
  const [openAccordion, setOpenAccordion] = useState<string | undefined>(() => {
    if (typeof window === 'undefined') {
      return 'providers' // SSR fallback or if localStorage is not available
    }
    const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (storedValue === null) {
      // No value in localStorage, default to 'providers' (e.g., first visit)
      return 'providers'
    }
    if (storedValue === '') {
      // Empty string means all accordions were explicitly closed
      return undefined
    }
    return storedValue // A specific section was stored
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (openAccordion) {
      localStorage.setItem(LOCAL_STORAGE_KEY, openAccordion)
    } else {
      // openAccordion is undefined, meaning all are collapsed
      localStorage.setItem(LOCAL_STORAGE_KEY, '')
    }
  }, [openAccordion])

  const providerPillLinks = [
    {
      text: 'Training Modules',
      href: '/toolkit/providers/training'
    },
    {
      text: 'Extension for Community Health Outcomes',
      href: '/toolkit/providers/echo'
    },
    {
      text: 'Educational Resources',
      href: '/toolkit/providers/educational-resources'
    },
    {
      text: 'Burnout Solutions',
      href: '/toolkit/providers/burnout-solutions'
    },
    {
      text: 'Patient Specific Treatment Plans',
      href: '/toolkit/providers/treatment-plans'
    }
  ]

  const clinicPillLinks = [
    {
      text: 'Training Modules',
      href: '/toolkit/clinics/training'
    },
    {
      text: 'Educational Resources',
      href: '/toolkit/clinics/educational-resources'
    },
    {
      text: 'Patient Specific Treatment Plans',
      href: '/toolkit/clinics/treatment-plans'
    },
    {
      text: 'Extension for Community Health Outcomes (ECHO)',
      href: '/toolkit/clinics/echo'
    },
    {
      text: 'Common Elements of an Infusion Center',
      href: '/toolkit/clinics/common-elements'
    },
    {
      text: 'List of Local Resources',
      href: '/toolkit/clinics/local-resources'
    },
    {
      text: 'Patient Education Materials',
      href: '/toolkit/clinics/patient-education'
    },
    {
      text: 'Audit & Feedback',
      href: '/toolkit/clinics/audit-feedback'
    },
    {
      text: 'Staff Review and Job Descriptions',
      href: '/toolkit/clinics/staff-reviews'
    }
  ]

  const communityPillLinks = [
    {
      text: 'Community-based Resources',
      href: '/toolkit/community/community-based-resources'
    },
    {
      text: 'Limited SCD Providers',
      href: '/toolkit/community/limited-scd-providers'
    },
    {
      text: 'Collaborating with Political Leaders and Government Officials',
      href: '/toolkit/community/political-collaboration'
    },
    {
      text: 'Lack of Awareness of SCD or Stigma Towards SCD',
      href: '/toolkit/community/scd-awareness-stigma'
    }
  ]

  const institutionsPillLinks = [
    {
      text: 'Sickle Cell Disease Team & Emergency Department Relationship',
      href: '/toolkit/institution/scd-team-ed-relationship'
    },
    {
      text: 'Resources for Building a Sickle Cell Infusion Clinic (IC)',
      href: '/toolkit/institution/building-ic-resources'
    }
  ]

  return (
    <ToolkitPageLayout
      title="Welcome to the Sickle Cell Infusion Clinic Toolkit"
      showBreadcrumbs={false}
      data-testid="toolkit-home-container"
    >
      {/* Main Content: Intro Text + Accordions */}
      <div className="mb-12">
        {/* Introductory Text */}
        <div className="prose text-lg text-[#3C2416] max-w-none mb-8">
          <p>
            The purpose of the Sickle Cell Disease (SCD) Infusion Clinic Toolkit
            is to build the capacity to expand access to Infusion Clinics (ICs)
            and improve care. This toolkit was developed through a multi-center
            study funded by Patient-Centered Outcomes Research Institute
            (PCORI). Through the identification of barriers and facilitators, we
            have identified mitigating strategies and developed this infusion
            clinic toolkit aimed at providers, clinical staff, hospital
            administration, and community stakeholders (including people living
            with SCD) to support the infusion clinic model.
          </p>
        </div>

        {/* Important Notice */}
        <div className="bg-[#EDE6DE] border-l-4 border-[#A6410A] p-6 mb-8 rounded-r-lg">
          <p className="text-lg text-[#3C2416]">
            <strong>Please note:</strong> Your infusion clinic documents,
            procedures and policies will be based on the resources, staffing and
            your institutional operations. The infusion clinic documents,
            procedures and policy samples are meant to be used as a guide when
            planning your own infusion center operations.
          </p>
        </div>

        {/* Accordions - Full Width */}
        <div className="space-y-6">
          <Accordion.Root
            type="single"
            collapsible
            className="space-y-4"
            value={openAccordion}
            onValueChange={setOpenAccordion}
          >
            <AccordionItem
              value="providers"
              title="Providers"
              titleIcon={<img src={Stethoscope} className="w-6 h-6" />}
              description={
                <Link
                  to="/toolkit/providers"
                  className="hover:underline"
                  onClick={() => setOpenAccordion('providers')}
                >
                  <p className="text-[#713d43]">
                    Are you a provider looking for tools to improve the care of
                    people living with SCD? The provider level toolkit items
                    focus on training and education on SCD management and unique
                    aspects of working with SCD patients and families including
                    cultural competency, social determinants, pain management
                    strategies, newly approved treatments as well as strategies
                    to prevent and manage burnout.
                  </p>
                </Link>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {providerPillLinks.map((pillLink, index) => (
                  <Link
                    to={pillLink.href}
                    className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-4 py-3 text-center text-sm font-medium transition-colors duration-200"
                    key={index}
                    onClick={() => setOpenAccordion('providers')}
                  >
                    {pillLink.text}
                  </Link>
                ))}
              </div>
            </AccordionItem>

            <AccordionItem
              value="clinics"
              title="Clinics"
              titleIcon={<img src={AmbulatoryClinic} className="w-6 h-6" />}
              description={
                <Link
                  to="/toolkit/clinics"
                  className="hover:underline"
                  onClick={() => setOpenAccordion('clinics')}
                >
                  <p className="text-[#713d43]">
                    The clinic level toolkit items focus on the infusion clinic
                    (IC) staff and the common elements of operating an IC such
                    as clinical workflows, team building, staff reviews, audit
                    and feedback on IC operations, case management and care
                    management roles in the IC.
                  </p>
                </Link>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {clinicPillLinks.map((pillLink, index) => (
                  <Link
                    to={pillLink.href}
                    className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-4 py-3 text-center text-sm font-medium transition-colors duration-200"
                    key={index}
                    onClick={() => setOpenAccordion('clinics')}
                  >
                    {pillLink.text}
                  </Link>
                ))}
              </div>
            </AccordionItem>

            <AccordionItem
              value="community"
              title="Community"
              titleIcon={
                <img src={CommunityHealthcareWorker} className="w-6 h-6" />
              }
              description={
                <Link
                  to="/toolkit/community"
                  className="hover:underline"
                  onClick={() => setOpenAccordion('community')}
                >
                  <p className="text-[#713d43]">
                    The community level toolkit items include information on
                    collaborating with community based organizations (CBOs),
                    community health workers (CHWs), political leaders and
                    government officials as well as strategies to address lack
                    of awareness of SCD and stigma.
                  </p>
                </Link>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {communityPillLinks.map((pillLink, index) => (
                  <Link
                    to={pillLink.href}
                    className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-4 py-3 text-center text-sm font-medium transition-colors duration-200"
                    key={index}
                    onClick={() => setOpenAccordion('community')}
                  >
                    {pillLink.text}
                  </Link>
                ))}
              </div>
            </AccordionItem>

            <AccordionItem
              value="institution"
              title="Institution"
              titleIcon={<img src={Clinic} className="w-6 h-6" />}
              description={
                <Link
                  to="/toolkit/institution"
                  className="hover:underline"
                  onClick={() => setOpenAccordion('institution')}
                >
                  <p className="text-[#713d43]">
                    The institution level toolkit items focus on policies,
                    fostering relationships between SCD teams and emergency
                    departments, and resources for building and operating a
                    Sickle Cell Infusion Clinic.
                  </p>
                </Link>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {institutionsPillLinks.map((pillLink, index) => (
                  <Link
                    to={pillLink.href}
                    className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-4 py-3 text-center text-sm font-medium transition-colors duration-200"
                    key={index}
                    onClick={() => setOpenAccordion('institution')}
                  >
                    {pillLink.text}
                  </Link>
                ))}
              </div>
            </AccordionItem>
          </Accordion.Root>
        </div>
      </div>

      {/* Information Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {/* What is Sickle Cell Disease */}
        <div className="bg-[#3C2416] text-white rounded-lg overflow-hidden shadow-lg flex flex-col">
          <img
            src={SickleCellBlood}
            alt="Illustration of sickle cells"
            className="w-full h-48 object-cover"
          />
          <div className="p-8">
            <h3 className="text-2xl font-bold mb-6 text-center text-[#EDE6DE]">
              What is Sickle Cell Disease?
            </h3>
            <p className="text-lg leading-relaxed">
              Sickle cell disease (SCD) is a genetic disorder of the blood that
              affects the red blood cell. This inheritable mutation of the red
              blood cell leads to a crescent or &quot;sickle&quot; shaped cell.
              This causes acute and chronic pain and leads to severe morbidity
              and early mortality affecting about 100,000 individuals in the
              United States, primarily African-Americans. The best-known burden
              of SCD is the vaso-occlusive crisis (VOC). These acute painful
              events are the leading cause of hospital and emergency department
              (ED) utilization. The good news is infusion clinics (IC) have been
              shown to improve outcomes in the management of patients with
              uncomplicated VOC when compared to ED management.
            </p>
          </div>
        </div>

        {/* Why Infusion Clinic Model */}
        <div className="bg-[#142841] text-white rounded-lg overflow-hidden shadow-lg flex flex-col">
          <img
            src={BloodCloseUp}
            alt="Close up of blood cells"
            className="w-full h-48 object-cover"
          />
          <div className="p-8">
            <h3 className="text-2xl font-bold mb-6 text-center text-[#EDE6DE]">
              Why is an Infusion Clinic care model important for SCD?
            </h3>
            <p className="text-lg leading-relaxed">
              Studies show that people with SCD and healthcare providers are
              dissatisfied with the quality of SCD pain management in the
              Emergency Department (ED). ED care for people living with SCD is
              marked by long delays, lack of efficacy, and conflict. Nationally,
              40% of ED visits for SCD pain result in a hospital admission.
              Further, individuals with SCD report not having enough involvement
              in decisions about their own care and that providers do not
              demonstrate respect, trust, and compassion. Additionally, research
              shows that providers are strongly predisposed to suspect addiction
              in SCD individuals presenting for acute pain management. The good
              news is that infusion clinics (IC) are proven alternatives to
              ED&apos;s for the management of acute pain.
            </p>
          </div>
        </div>
      </div>

      {/* Goals Section */}
      <div className="bg-[#EDE6DE] rounded-lg p-8 mb-12">
        <h2 className="text-3xl font-bold text-[#3C2416] mb-8 text-center">
          What are the goals of the infusion clinic toolkit?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Specialized Care */}
          <div className="text-center flex flex-col">
            <img
              src={PatientAndPractionerTalking}
              alt="Patient and practioner talking in room"
              className="w-full h-40 object-cover rounded-t-lg"
            />
            <div className="bg-[#3C2416] text-white rounded-b-lg p-6 flex-grow">
              <h3 className="text-lg font-bold mb-2">Specialized Care</h3>
              <p className="text-sm">
                Infusion clinics provide specialized care tailored to the unique
                needs of patients with sickle cell disease, ensuring better
                outcomes.
              </p>
            </div>
          </div>
          {/* Reduced Wait Times */}
          <div className="text-center flex flex-col">
            <img
              src={SmilingDoctorCloseUp}
              alt="Smiling doctor close up"
              className="w-full h-40 object-cover rounded-t-lg"
            />
            <div className="bg-[#142841] text-white rounded-b-lg p-6 flex-grow">
              <h3 className="text-lg font-bold mb-2">Reduced Wait Times</h3>
              <p className="text-sm">
                Patients experience significantly shorter wait times compared to
                emergency departments, leading to faster pain relief.
              </p>
            </div>
          </div>
          {/* Patient-Centered Approach */}
          <div className="text-center flex flex-col">
            <img
              src={SmilingSittingDoctor}
              alt="Smiling sitting doctor"
              className="w-full h-40 object-cover rounded-t-lg"
            />
            <div className="bg-[#A6410A] text-white rounded-b-lg p-6 flex-grow">
              <h3 className="text-lg font-bold mb-2">
                Patient-Centered Approach
              </h3>
              <p className="text-sm">
                Infusion clinics foster a patient-centered approach where
                individuals have more involvement in decisions about their care.
              </p>
            </div>
          </div>
          {/* Reduced Hospitalizations */}
          <div className="text-center flex flex-col">
            <img
              src={SmilingPatientOnBed}
              alt="Smiling patient on bed"
              className="w-full h-40 object-cover rounded-t-lg"
            />
            <div className="bg-[#AF8A48] text-white rounded-b-lg p-6 flex-grow">
              <h3 className="text-lg font-bold mb-2">
                Reduced Hospitalizations
              </h3>
              <p className="text-sm">
                Effective management in infusion clinics leads to fewer hospital
                admissions, reducing healthcare costs and improving quality of
                life.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
